import { ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useToast } from 'vue-toast-notification';

import { ModalName } from '@/constants/modals';
import { useAuthStore } from '@/modules/auth/store/authStore';
import missionsService from '@/modules/missions/services/missionsService';
import { type IMissionDB, MissionCommand } from '@/types/mission';

import { useModalStore } from '@/dsComponents/modals/modalStore';

interface UseCloseOrderOptions {
	onSuccess?: (updatedMission: IMissionDB) => void | Promise<void>;
	// biome-ignore lint/suspicious/noExplicitAny: Init Biome
	onError?: (error: any) => void;
}

export const useCloseOrder = (options: UseCloseOrderOptions = {}) => {
	const { t } = useI18n();
	const authStore = useAuthStore();
	const toaster = useToast();
	const modalStore = useModalStore();

	const evoliaSendInProgress = ref(false);
	const { onSuccess, onError } = options;

	const sendOrderToEvolia = async (
		contractManagerIdentifier: string,
		missionId: string
	) => {
		try {
			if (!contractManagerIdentifier) {
				throw new Error('Contract manager identifier is required');
			}

			evoliaSendInProgress.value = true;
			const updatedMission = await missionsService.sendCommandMission({
				missionId,
				command: MissionCommand.sendOrderToEvolia,
				data: { contractManagerIdentifier },
			});

			if (onSuccess) {
				await onSuccess(updatedMission);
			}

			toaster?.open({
				message: t('modals.SendOrderToEvolia.success.sent'),
				type: 'success',
				duration: 5000,
			});
		} catch (err) {
			// biome-ignore lint/suspicious/noExplicitAny: Init Biome
			const error: any = err;
			console.error('error:', err);

			if (onError) {
				onError(error);
			}

			if (error._global?.[0]?.id?.includes('Mission')) {
				toaster?.open({
					// biome-ignore lint/style/useTemplate: Init Biome
					message: t('errors.' + error._global[0].id + '._default') as string,
					type: 'error',
					duration: 10000,
				});
			} else {
				toaster?.open({
					message: t('errors.Global.ServerError._default'),
					type: 'error',
					duration: 10000,
				});
			}
		} finally {
			modalStore.actions.close(ModalName.SendOrderToEvolia);
			evoliaSendInProgress.value = false;
		}
	};

	const closeMissionWithoutSendToEvolia = async (missionId: string) => {
		try {
			evoliaSendInProgress.value = true;
			const updatedMission = await missionsService.sendCommandMission({
				missionId,
				command: MissionCommand.complete,
			});

			if (onSuccess) {
				await onSuccess(updatedMission);
			}

			// All agency users have evolia orders enabled
			const hasEvoliaOrders = authStore.isAgency;
			toaster?.open({
				message: t(
					hasEvoliaOrders
						? 'modals.SendOrderToEvolia.success.closedWithoutSend'
						: 'modals.SendOrderToEvolia.success.closed'
				),
				type: 'success',
				duration: 5000,
			});
		} catch (err) {
			console.error(err);

			if (onError) {
				onError(err);
			}

			toaster?.open({
				message: t('errors.Global.ServerError._default'),
				type: 'error',
				duration: 10000,
			});
		} finally {
			evoliaSendInProgress.value = false;
			modalStore.actions.close(ModalName.SendOrderToEvolia);
		}
	};

	return {
		sendOrderToEvolia,
		closeMissionWithoutSendToEvolia,
		evoliaSendInProgress,
	};
};
